import React, { useCallback, useEffect, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import 'react-phone-input-2/lib/plain.css';

import { Theme } from '@app/theme';
import { Input } from './Input';
import { PromoCodeVerification, useVerifyPromoCode } from '@app/api';
import { IconChip, Link } from '@app/core';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  verify: {
    ...theme.fonts.label,
    color: theme.colors.blue,
  },
  error: {
    ...theme.fonts.caption,
    color: theme.colors.red,
  },
}));

interface Props {
  code?: string;
}

export const PromoCodeInput = ({ code }: Props) => {
  const theme = useTheme() as Theme;
  const classes = useStyles({ theme });
  const { t } = useTranslation('common');
  const [verifiedPromoCode, setVerifiedPromoCode] = useState<
    PromoCodeVerification | undefined
  >(undefined);
  const { values, setFieldValue } = useFormikContext<any>();
  const { mutateAsync: verify } = useVerifyPromoCode();

  const verifyPromoCode = useCallback(
    async (code: string) => {
      const result = await verify({ code });
      setVerifiedPromoCode(result);
    },
    [verify],
  );

  useEffect(() => {
    if (!code) {
      setVerifiedPromoCode(undefined);
    }
  }, [code, values.promoCode]);

  useEffect(() => {
    if (!code) {
      return;
    }
    (async () => {
      const result = await verify({ code });
      setVerifiedPromoCode(result);
      setFieldValue('promoCode', code);
    })();
  }, [code, setFieldValue, verify]);

  return (
    <div>
      <Input
        name="promoCode"
        label={t('clients.label.promoCode')}
        placeholder={t('clients.placeholder.promoCode')}
        disabled={!!code}
        error={
          verifiedPromoCode &&
          !verifiedPromoCode.isValid &&
          verifiedPromoCode.error
            ? t(`form.errors.promoCode.${verifiedPromoCode.error}`)
            : null
        }
        rightAction={
          <div className={classes.container}>
            {verifiedPromoCode && (
              <>
                {verifiedPromoCode?.isValid && (
                  <IconChip icon="ri-check-line" color="green" />
                )}
              </>
            )}
            {values.promoCode &&
              values.promoCode.length &&
              !verifiedPromoCode && (
                <Link
                  onClick={() => verifyPromoCode(values.promoCode)}
                  type="button"
                >
                  <div className={classes.verify}>{t('form.verify')}</div>
                </Link>
              )}
          </div>
        }
      />
    </div>
  );
};
